import { useState } from 'react';
import { Input, Card, Heading, BodyText, Button } from '@aaimplatform/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { PercentInput } from './PercentInput';

type ConcentrationPolicy = components['schemas']['ConcentrationPolicy'];
type SectorConcentrationOverride = components['schemas']['SectorConcentrationOverride'];

interface ConcentrationTabProps {
  policy: ConcentrationPolicy;
  onChange: (policy: ConcentrationPolicy) => void;
}

/** Sector ids compare trimmed and case-insensitively, so PRECIOUS_METALS and precious_metals are one sector. */
function sameSectorId(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * A percent limit field at a fixed width. The width sits on a non-shrinking
 * wrapper because the kit Input's own wrapper is full width and a width class
 * passed to it sizes only the inner <input>; inside a justify-between row the
 * old wrapper shrank to 58-63 px on phone and clipped "25" to "2" (UI run
 * 2026-09-27, fi-g7-7). aria-labelledby names the field by the visible label
 * beside it (fi-g7-10).
 */
function LimitInput({
  labelId,
  value,
  onChange,
}: {
  labelId: string;
  value: number;
  onChange: (value: number) => void;
}) {
  // PercentInput keeps the typed text while the field has focus and shows the
  // same one-decimal precision as the other policy editors; the whole-number
  // format turned a stored 12.5 into 13 and reset '12.' to 0 (r3 fi-g7-2).
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-24">
        <PercentInput
          aria-labelledby={labelId}
          value={value}
          onChange={(ratio) => {
            if (ratio !== undefined) onChange(ratio);
          }}
          min={1}
          max={100}
          className="text-right font-mono"
        />
      </div>
      <span className="text-sm text-tertiary" aria-hidden="true">%</span>
    </div>
  );
}

/**
 * ConcentrationTab - Portfolio-level exposure controls
 *
 * Limits how much of total pledged collateral can concentrate in any single
 * position, issuer, sector, or asset class. Sector overrides allow tighter
 * limits for specific sectors (e.g., crypto).
 */
export function ConcentrationTab({ policy, onChange }: ConcentrationTabProps) {
  const [newSectorId, setNewSectorId] = useState('');
  const [newSectorName, setNewSectorName] = useState('');
  // The Add row requires its own limit; a new override was created at a 25%
  // the admin never entered (r3 fi-g7-13).
  const [newSectorMaxPct, setNewSectorMaxPct] = useState<number | undefined>(undefined);

  const overrides = policy.sectorOverrides || [];
  const duplicateSector =
    newSectorId.trim() !== '' && overrides.some((o) => sameSectorId(o.sectorId, newSectorId));
  const newLimitValid =
    newSectorMaxPct !== undefined && newSectorMaxPct > 0 && newSectorMaxPct <= 1;
  const canAdd =
    newSectorId.trim() !== '' && newSectorName.trim() !== '' && newLimitValid && !duplicateSector;

  const handleFieldChange = (field: keyof ConcentrationPolicy, value: number) => {
    onChange({ ...policy, [field]: value });
  };

  const handleOverrideChange = (index: number, field: keyof SectorConcentrationOverride, value: string | number) => {
    const updated = (policy.sectorOverrides || []).map((o, i) =>
      i === index ? { ...o, [field]: value } : o,
    );
    onChange({ ...policy, sectorOverrides: updated });
  };

  const handleAddOverride = () => {
    // A duplicate sector id collided on the row key and put two entries for
    // one sector in the policy (r3 fi-g7-13).
    if (!canAdd || newSectorMaxPct === undefined) return;
    const override: SectorConcentrationOverride = {
      sectorId: newSectorId.trim(),
      sectorName: newSectorName.trim(),
      maxPct: newSectorMaxPct,
    };
    onChange({
      ...policy,
      sectorOverrides: [...overrides, override],
    });
    setNewSectorId('');
    setNewSectorName('');
    setNewSectorMaxPct(undefined);
  };

  const handleRemoveOverride = (index: number) => {
    const updated = (policy.sectorOverrides || []).filter((_, i) => i !== index);
    onChange({ ...policy, sectorOverrides: updated });
  };

  return (
    <div className="space-y-6">
      {/* Page title lives in the sticky topnav (AppHeader); no redundant H1.
          Reset and Save sit in one row on CollateralPolicyPage (r3 fi-g7-11). */}

      {/* Global limits */}
      <Card>
        <div className="px-5 py-4">
          {/* Semantic level 2: the page h1 is the AppHeader title, and the two
              card titles are the first headings beneath it. as={4} keeps the
              sans card-title face the level-4 heading had. */}
          <Heading level={2} as={4} size="card" className="mb-5">Portfolio Concentration Limits</Heading>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            Maximum percentage of total pledged collateral that can be concentrated
            in any single position, issuer, sector, or asset class. These are
            portfolio-level guardrails that apply across all pledges.
          </BodyText>

          <div className="space-y-5">
            <div className="flex items-center justify-between py-3 border-b border-recessed gap-4">
              <div className="min-w-0">
                <BodyText variant="ui" size="sm" id="maxSinglePositionPct-label">Single position limit</BodyText>
                <BodyText variant="ui" size="xs" color="tertiary">
                  Max portfolio exposure to any one holding
                </BodyText>
              </div>
              <LimitInput
                labelId="maxSinglePositionPct-label"
                value={policy.maxSinglePositionPct}
                onChange={(value) => handleFieldChange('maxSinglePositionPct', value)}
              />
            </div>

            <div className="flex items-center justify-between py-3 border-b border-recessed gap-4">
              <div className="min-w-0">
                <BodyText variant="ui" size="sm" id="maxSingleIssuerPct-label">Single issuer limit</BodyText>
                <BodyText variant="ui" size="xs" color="tertiary">
                  Max portfolio exposure to any one issuer
                </BodyText>
              </div>
              <LimitInput
                labelId="maxSingleIssuerPct-label"
                value={policy.maxSingleIssuerPct}
                onChange={(value) => handleFieldChange('maxSingleIssuerPct', value)}
              />
            </div>

            <div className="flex items-center justify-between py-3 border-b border-recessed gap-4">
              <div className="min-w-0">
                <BodyText variant="ui" size="sm" id="maxSingleSectorPct-label">Single sector limit</BodyText>
                <BodyText variant="ui" size="xs" color="tertiary">
                  Max portfolio exposure to any one sector
                </BodyText>
              </div>
              <LimitInput
                labelId="maxSingleSectorPct-label"
                value={policy.maxSingleSectorPct}
                onChange={(value) => handleFieldChange('maxSingleSectorPct', value)}
              />
            </div>

            <div className="flex items-center justify-between py-3 gap-4">
              <div className="min-w-0">
                <BodyText variant="ui" size="sm" id="maxSingleAssetClassPct-label">Single asset class limit</BodyText>
                <BodyText variant="ui" size="xs" color="tertiary">
                  Max portfolio exposure to any one asset class
                </BodyText>
              </div>
              <LimitInput
                labelId="maxSingleAssetClassPct-label"
                value={policy.maxSingleAssetClassPct}
                onChange={(value) => handleFieldChange('maxSingleAssetClassPct', value)}
              />
            </div>
          </div>

          {/* The hand-built 'Limit summary' tiles were removed (r3 fi-g7-15):
              they restated the four inputs directly above them. */}
        </div>
      </Card>

      {/* Sector overrides */}
      <Card>
        <div className="px-5 py-4">
          <Heading level={2} as={4} size="card" className="mb-2">Sector Overrides</Heading>
          <BodyText variant="ui" size="sm" color="secondary" className="mb-5">
            Override the default sector limit for specific sectors. For example,
            set a tighter limit on digital assets or a looser limit on
            government securities.
          </BodyText>

          {overrides.length > 0 && (
            <div className="space-y-3 mb-5">
              {overrides.map((override, index) => (
                <div
                  // The index keeps the key unique for a stored policy that
                  // already carries a repeated sector id (r3 fi-g7-13).
                  key={`${override.sectorId}:${index}`}
                  className="flex items-center gap-4 py-3 border-b border-recessed last:border-0"
                >
                  {/* The sector id is an identifier, not copy; it rides on the
                      title attribute (fi-g7-13). */}
                  <div className="flex-1 min-w-0" title={override.sectorId}>
                    <BodyText variant="ui" size="sm" id={`sector-override-${index}-label`}>
                      {override.sectorName}
                    </BodyText>
                  </div>
                  <LimitInput
                    labelId={`sector-override-${index}-label`}
                    value={override.maxPct}
                    onChange={(value) => handleOverrideChange(index, 'maxPct', value)}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveOverride(index)}
                    className="text-tertiary hover:text-error transition-colors p-1 shrink-0"
                    title="Remove override"
                    aria-label={`Remove ${override.sectorName} override`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add override */}
          <div className="flex flex-col gap-3 pt-3 border-t border-recessed sm:flex-row sm:items-start">
            <div className="flex-1">
              <label htmlFor="new-sector-id" className="block text-label-xs font-semibold text-tertiary uppercase mb-1">
                Sector ID
              </label>
              <Input
                id="new-sector-id"
                value={newSectorId}
                onChange={(e) => setNewSectorId(e.target.value)}
                placeholder="e.g., PRECIOUS_METALS"
                error={
                  duplicateSector
                    ? 'This sector already has an override. Change its limit above instead.'
                    : undefined
                }
                className="text-sm"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="new-sector-name" className="block text-label-xs font-semibold text-tertiary uppercase mb-1">
                Sector Name
              </label>
              <Input
                id="new-sector-name"
                value={newSectorName}
                onChange={(e) => setNewSectorName(e.target.value)}
                placeholder="e.g., Precious Metals"
                className="text-sm"
              />
            </div>
            <div className="w-full shrink-0 sm:w-28">
              <label htmlFor="new-sector-max-pct" className="block text-label-xs font-semibold text-tertiary uppercase mb-1">
                Max %
              </label>
              <PercentInput
                id="new-sector-max-pct"
                optional
                value={newSectorMaxPct}
                onChange={setNewSectorMaxPct}
                min={1}
                max={100}
                placeholder="Required"
                className="text-sm text-right font-mono"
              />
            </div>
            <div className="shrink-0">
              {/* Holds the button level with the inputs beneath their labels. */}
              <span aria-hidden="true" className="hidden sm:block text-label-xs mb-1">&nbsp;</span>
              <Button
                onClick={handleAddOverride}
                disabled={!canAdd}
                variant="secondary"
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
